/* global MediaElement */
import 'mediaelement';
import 'mediaelement/build/flashmediaelement.swf';
import isElement from 'lodash/isElement';
import isNaN from 'lodash/isNaN';
import noop from 'lodash/noop';
import url from 'url';

import EventCoordinator from '../../../../../../tools/EventCoordinator';
import * as AudioConstants from '../../../constants';
import * as D from '../../../deps';

export const NAME = AudioConstants.AudioHarnessName.MEDIA_ELEMENT;

const singleAudioElement = new Audio();
let browserAutoplayBlocked = false;

const MEDIAELEMENT_ERROR_MAPPING = {
  0: {
    errMsg: 'Browser autoplay blocked',
    commonType: AudioConstants.CommonAudioErrorCodes.BROWSER_AUTOPLAY_BLOCKED,
  },
  1: {
    errMsg: 'User aborted playback',
    commonType: AudioConstants.CommonAudioErrorCodes.ABORTED,
  },
  2: {
    errMsg: 'A network error caused download to fail',
    commonType: AudioConstants.CommonAudioErrorCodes.NETWORK,
  },
  3: {
    errMsg: 'The media was either corrupted or not supported by the browser',
    commonType: AudioConstants.CommonAudioErrorCodes.DECODE,
  },
  4: {
    errMsg:
      'The media could not be loaded, either because the server or network failed or because the format is not supported.',
    commonType: AudioConstants.CommonAudioErrorCodes.SRC_NOT_SUPPORTED,
  },
};

function handleError(playerEl = {}, handlerFn) {
  const error = MEDIAELEMENT_ERROR_MAPPING[playerEl.error && playerEl.error.code] || {
    errMsg: `An unknown media error occurred ${playerEl.error}`,
    commonType: AudioConstants.CommonAudioErrorCodes.UNKNOWN,
  };
  handlerFn(new Error(JSON.stringify({ message: error.errMsg, commonType: error.commonType })));
}

const log = D.Logger.child({
  component: 'mediaElementHarness',
});

const VOLUME_MULTIPLIER = 0.01;
const ENABLED = 'enabled';
const DISABLED = 'disabled';

// keeping track of what handler functions are being used
const eventer = new EventCoordinator();
let counter = 0;

export function initialize(audioSrc, resolve, reject, options = {}) {
  const srcUrl = `${audioSrc.url}${audioSrc.url.includes('?') ? '&' : '?'}av=${__VERSION__}`;
  const format = audioSrc.format;
  const forceFlash = options.tryForceFlash;
  const reuseElements = !!options.reuseElements;
  const reuseElementsDataAttr = reuseElements ? ENABLED : DISABLED;

  const parsed = url.parse(srcUrl);
  if (!parsed.protocol || !parsed.hostname) {
    log.trace('Received relative URL as audio source! Might not work as expected: ', srcUrl);
  }

  const audio = reuseElements ? singleAudioElement : new Audio();

  audio.setAttribute('id', `mediaelement_${counter}`);
  counter++;
  document.body.appendChild(audio);

  let timedOut = false;
  const timeout = setTimeout(() => {
    timedOut = true;
    reject(
      new Error(
        JSON.stringify({
          message: 'Player initialization timed out',
          commonType: AudioConstants.CommonAudioErrorCodes.INIT_TIMEOUT,
        }),
      ),
    );
  }, AudioConstants.INIT_TIMEOUT_MS);

  const baseConfig = {
    mode: forceFlash ? 'auto_plugin' : 'auto',
    // shows debug errors on screen
    enablePluginDebug: false,
    // remove or reorder to change plugin priority
    plugins: ['flash'],
    // specify to force MediaElement to use a particular video or audio type
    // when playing aac
    type: AudioConstants.CommonAudioFormatToMediaElementTypes[format],
    // you have to add this for flash to work with aac
    // pluginVars: 'isvideo=true',
    // path to Flash and Silverlight plugins
    pluginPath: ASSET_LOCATION,
    // name of flash file
    flashName: 'flashmediaelement.swf',
    // default if the <video width> is not specified
    defaultVideoWidth: 480,
    // default if the <video height> is not specified
    defaultVideoHeight: 270,
    // overrides <video width>
    pluginWidth: -1,
    // overrides <video height>
    pluginHeight: -1,
    // rate in milliseconds for Flash and Silverlight to fire the timeupdate event
    // larger number is less accurate, but less strain on plugin->JavaScript bridge
    timerRate: 250,
    // method that fires when the Flash or Silverlight object is ready
    success: (mediaElement, domObject) => {
      mediaElement.onerror = event => {
        log.trace('mediaElement.onError success handler: ', event && event.message);
        handleError(event.currentTarget, options.defaultError);
      };
      if (timedOut) {
        return;
      }
      clearTimeout(timeout);
      domObject.src = srcUrl;
      domObject.setAttribute('data-reuse-elements', reuseElementsDataAttr);
      resolve(domObject);
    },
    // fires when a problem is detected
    error: err => {
      log.trace('mediaElement error handler: ', err && err.message);
      clearTimeout(timeout);
      if (isElement(err) && err.src) {
        if (timedOut) {
          return;
        }
        err.src = srcUrl;
        if (err.setAttribute) err.setAttribute('data-reuse-elements', reuseElementsDataAttr);
        // Media element will return the <audio> element here if plugins fail
        resolve(err);
      }
      reject(err);
    },
  };

  // Return value not really used, but satisfying linter
  return new MediaElement(audio, baseConfig);
}

export function mapMethods(player, options) {
  return {
    play() {
      // reset browserAutoplayBlocked only when we've received an explicit intent to play
      browserAutoplayBlocked = false;
      const playResult = player.play();
      if (playResult) {
        // This is for chrome and safari to avoid logs to the console for things
        // that should be caught by listening to the error event
        playResult.catch(e => {
          log.info(e.message);
          // Browser has blocked autoplayback
          if (e.name && e.name === AudioConstants.BROWSER_AUTOPLAY_BLOCKED_PROMISE_ERROR) {
            browserAutoplayBlocked = true;
            handleError({ error: { code: 0 } }, options.defaultError);
          }
        });
      }
    },
    pause() {
      player.pause();
    },
    setVolume(volume) {
      player.volume = volume * VOLUME_MULTIPLIER;
    },
    setCurrentTime(position) {
      if (!isNaN(player.duration)) {
        player.currentTime = position;
      } else {
        player.addEventListener('loadedmetadata', function handler() {
          player.currentTime = position;
          player.removeEventListener('loadedmetadata', handler);
        });
      }
    },
    setMuted(isMuted) {
      player.muted = isMuted;
    },
    destroy() {
      const parentNode = player.parentNode;
      // using the attribute `data-reuse-elements`
      const shouldReuse = player.dataset && player.dataset.reuseElements;

      if (shouldReuse === ENABLED) return;

      if (parentNode) {
        parentNode.removeChild(player);
        // Deleting the media element player doesn't delete the flash plugin
        // so we need to clean it up.
        const potentialPlugin = parentNode.getElementsByClassName('me-plugin');
        // Don't want to delete the only one, since sometimes media element only loads
        // one plugin for several players, sometimes it loads one for each.
        if (potentialPlugin && potentialPlugin.length > 1) {
          parentNode.removeChild(potentialPlugin[potentialPlugin.length - 1]);
        }
      } else if (player.remove) {
        player.remove();
      } else {
        log.error('Failed to remove media element from DOM');
      }
    },
    getState() {
      return {
        readyState: player.readyState,
        currentTime: player.currentTime,
        buffered: player.buffered,
        duration: player.duration,
        ended: player.ended,
        error: player.error,
        muted: player.muted,
        paused: player.paused,
        played: player.played,
        src: player.src,
      };
    },
  };
}

export function mapEventBindings(player) {
  // we assign an error handler explicitly to handler errors that occur
  // between initialization and when we bind event handlers (namely this is meant to handle
  // errors that arise when assigning an audio url). Here is where we remove that
  // so we don't double fire errors.
  if (player.onerror) {
    player.onerror = noop;
  }
  return {
    play(handlerFn) {
      eventer.onOnly(player, 'play', handlerFn);
    },

    pause(handlerFn) {
      eventer.onOnly(player, 'pause', handlerFn);
    },

    playing(handlerFn) {
      eventer.onOnly(player, 'playing', event => {
        // mediaelement emits multiple playing/pause events while browser autoplay is blocked.
        // browserAutoplayBlocked is used to ignore these unhelpful events from causing
        // causing our app to display incorrect audio player state.
        if (!browserAutoplayBlocked) {
          handlerFn(event);
        }
      });
    },

    stalled(handlerFn) {
      eventer.onOnly(player, 'stalled', handlerFn);
    },

    timeupdate(handlerFn) {
      eventer.onOnly(player, 'timeupdate', handlerFn);
    },

    durationchange(handlerFn) {
      eventer.onOnly(player, 'durationchange', handlerFn);
    },

    loadedmetadata(handlerFn) {
      eventer.onOnly(player, 'loadedmetadata', handlerFn);
    },

    canplaythrough(handlerFn) {
      eventer.onOnly(player, 'canplaythrough', handlerFn);
    },

    progress(handlerFn) {
      eventer.onOnly(player, 'progress', handlerFn);
    },

    ended(handlerFn) {
      eventer.onOnly(player, 'ended', handlerFn);
    },

    seeked(handlerFn) {
      eventer.onOnly(player, 'seeked', handlerFn);
    },

    error(handlerFn) {
      // @todo - Convention will be to have all error handlers pass an error object to the handlerFn
      // will need to enforce this convention, probably with unit tests
      eventer.onOnly(player, 'error', event => handleError(event.currentTarget, handlerFn));
    },

    playbackMetrics(handlerFn) {
      log.debug('playbackMetric(): not supported for mediaElementHarness');
    },
  };
}

export function mapEventRemovers(player) {
  return {
    play() {
      eventer.offOnly(player, 'play');
    },

    pause() {
      eventer.offOnly(player, 'pause');
    },

    playing() {
      eventer.offOnly(player, 'playing');
    },

    stalled() {
      eventer.offOnly(player, 'stalled');
    },

    timeupdate() {
      eventer.offOnly(player, 'timeupdate');
    },

    durationchange() {
      eventer.offOnly(player, 'durationchange');
    },

    loadedmetadata() {
      eventer.offOnly(player, 'loadedmetadata');
    },

    canplaythrough() {
      eventer.offOnly(player, 'canplaythrough');
    },

    progress() {
      eventer.offOnly(player, 'progress');
    },

    ended() {
      eventer.offOnly(player, 'ended');
    },

    seeked() {
      eventer.offOnly(player, 'seeked');
    },

    error() {
      eventer.offOnly(player, 'error');
    },

    playbackMetrics(handlerFn) {
      log.debug('playbackMetric(): not supported for mediaElementHarness');
    },
  };
}
